import React, { useState, useMemo, useRef, useEffect } from 'react';
import { getRequestDetail, getBatchReceived, getRequests, getBatches } from '../services/api';

// ── Field label maps ────────────────────────────────────────────────────────
const EXP_LABELS = {
  exp_id:            'Exp ID',
  award_number:      'Award #',
  awd_num:           'Award #',
  amount:            'Amount',
  amt:               'Amount',
  expenditure_category_long_name:              'Category',
  exp_cat:           'Category',
  expenditure_type_long_name:                  'Type',
  exp_type:          'Type',
  expenditure_high_level_subgroup_description: 'Subgroup',
  exp_subgroup:      'Subgroup',
  expenditure_transaction_description:         'Transaction Desc',
  exp_txn_desc:      'Transaction Desc',
  expenditure_transaction_long_description:    'Long Desc',
  exp_txn_long_desc: 'Long Desc',
  abbreviated_expenditure_transaction_description: 'Abbrev Desc',
  exp_txn_abbrev:    'Abbrev Desc',
  task_org:          'Task Org',
  task_org_long_name: 'Task Org Name',
  task_org_name:     'Task Org Name',
  project_name:      'Project Name',
  proj_name:         'Project Name',
  task_name:         'Task Name',
  award_long_name:   'Award Name',
  awd_name:          'Award Name',
  project_owner_full_name: 'Project Owner',
  proj_owner:        'Project Owner',
  task_owner_full_name:    'Task Owner',
  task_owner:        'Task Owner',
  award_owner_full_name:   'Award Owner',
  awd_owner:         'Award Owner',
};

const AWD_LABELS = {
  award_number:      'Award #',
  awd_num:           'Award #',
  award_org:         'Org',
  awd_org:           'Org',
  award_start_date:  'Start Date',
  awd_start:         'Start Date',
  award_long_name:   'Award Name',
  awd_name:          'Award Name',
  award_purpose_code: 'Purpose Code',
  awd_purpose:       'Purpose Code',
  award_level_of_restriction_description: 'Restriction',
  awd_restrict:      'Restriction',
  award_role_description:  'Role Desc',
  awd_role:          'Role Desc',
  award_role_employee_name: 'Role Employee',
  awd_role_emp:      'Role Employee',
};

// Each summary column is an array of fallback keys tried in order (long → short name)
const EXP_SUMMARY = [
  ['exp_id'],
  ['amount', 'amt'],
  ['expenditure_category_long_name', 'exp_cat'],
  ['expenditure_transaction_description', 'exp_txn_desc', 'abbreviated_expenditure_transaction_description', 'exp_txn_abbrev'],
  ['project_owner_full_name', 'proj_owner'],
  ['task_org_long_name', 'task_org_name', 'task_org'],
  ['project_name', 'proj_name'],
];

const AWD_SUMMARY = [
  ['award_number', 'awd_num'],
  ['award_long_name', 'awd_name'],
  ['award_org', 'awd_org'],
  ['award_purpose_code', 'awd_purpose'],
  ['award_level_of_restriction_description', 'awd_restrict'],
  ['award_role_employee_name', 'awd_role_emp'],
];

function labelOf(key) {
  return EXP_LABELS[key] || AWD_LABELS[key] || key;
}

// Resolve the first non-empty value from a list of candidate keys
function resolveVal(item, keys) {
  for (const k of keys) {
    const v = item[k];
    if (v != null && v !== '') return { key: k, value: v };
  }
  return { key: keys[0], value: null };
}

function truncate(val, n = 55) {
  if (val == null || val === '') return '—';
  const s = String(val);
  return s.length > n ? s.slice(0, n) + '…' : s;
}

// ── Expandable row ──────────────────────────────────────────────────────────
function DataRow({ item, summaryGroups, idx }) {
  const [open, setOpen] = useState(false);
  const allEntries = Object.entries(item).filter(([, v]) => v != null && v !== '');

  return (
    <>
      <tr
        onClick={() => setOpen(o => !o)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(o => !o); } }}
        tabIndex={0}
        aria-expanded={open}
        style={{
          cursor: 'pointer',
          background: open
            ? 'var(--accent-glow)'
            : idx % 2 === 0 ? 'var(--bg-card)' : 'var(--bg-secondary)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <td style={{ padding: '6px 8px', color: 'var(--text-muted)', fontSize: 11, width: 22, userSelect: 'none' }}>
          <span aria-hidden="true">{open ? '▼' : '▶'}</span>
        </td>
        {summaryGroups.map((keys, colIdx) => {
          const { key, value } = resolveVal(item, keys);
          const isFirst = colIdx === 0;
          const isAmount = keys.includes('amount') || keys.includes('amt');
          return (
            <td key={key} style={{
              padding: '6px 10px', fontSize: 12,
              maxWidth: isFirst ? 140 : 180,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              fontWeight: isFirst ? 700 : 400,
              color: value == null
                ? 'var(--text-muted)'
                : isFirst ? 'var(--text-primary)'
                : isAmount ? 'var(--green)'
                : 'var(--text-secondary)',
            }}>
              {isAmount && value != null
                ? `$${Number(value).toLocaleString()}`
                : truncate(value)}
            </td>
          );
        })}
      </tr>
      {open && (
        <tr style={{ background: 'var(--bg-secondary)' }}>
          <td colSpan={summaryGroups.length + 1} style={{ padding: '12px 18px 14px', borderBottom: '2px solid var(--accent)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '6px 28px' }}>
              {allEntries.map(([k, v]) => (
                <div key={k} style={{ fontSize: 12 }}>
                  <span style={{ color: 'var(--text-muted)', fontWeight: 600, marginRight: 4 }}>{labelOf(k)}:</span>
                  <span style={{ color: 'var(--text-primary)', wordBreak: 'break-word' }}>{String(v)}</span>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Data table ──────────────────────────────────────────────────────────────
function DataTable({ items, summaryGroups, emptyMsg }) {
  const [search, setSearch] = useState('');
  const [page, setPage]     = useState(0);
  const PAGE = 25;

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(item =>
      Object.values(item).some(v => v != null && String(v).toLowerCase().includes(q))
    );
  }, [items, search]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const slice = filtered.slice(page * PAGE, (page + 1) * PAGE);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <input
          className="form-input"
          placeholder="Search all fields…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          style={{ flex: 1, maxWidth: 300, fontSize: 12, padding: '5px 10px' }}
          aria-label="Search records"
        />
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {filtered.length.toLocaleString()} / {items.length.toLocaleString()} records
        </span>
        {pages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button className="btn btn-secondary" style={{ padding: '3px 8px', fontSize: 11 }}
              disabled={page === 0} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{page + 1} / {pages}</span>
            <button className="btn btn-secondary" style={{ padding: '3px 8px', fontSize: 11 }}
              disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)}>Next ›</button>
          </div>
        )}
      </div>

      {slice.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 30, textAlign: 'center' }}>{emptyMsg}</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)', borderBottom: '2px solid var(--border)' }}>
                <th style={{ width: 22 }} />
                {summaryGroups.map((keys) => (
                  <th key={keys[0]} style={{
                    padding: '7px 10px', textAlign: 'left', fontWeight: 700,
                    color: 'var(--text-muted)', whiteSpace: 'nowrap', fontSize: 11,
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                  }}>
                    {labelOf(keys[0])}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {slice.map((item, i) => (
                <DataRow
                  key={item.exp_id || item.award_number || i}
                  item={item}
                  summaryGroups={summaryGroups}
                  idx={i}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Recent picker dropdown ──────────────────────────────────────────────────
function RecentPicker({ sourceType, onPick }) {
  const [items, setItems]   = useState([]);
  const [open, setOpen]     = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    function handle(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    if (items.length === 0) {
      setLoading(true);
      try {
        if (sourceType === 'request') {
          const res = await getRequests(1, 30);
          setItems(res.requests || []);
        } else {
          const res = await getBatches(1, 30);
          setItems(res.batches || []);
        }
      } catch (_) {}
      setLoading(false);
    }
    setOpen(true);
  };

  // Reset when source type changes
  useEffect(() => { setItems([]); setOpen(false); }, [sourceType]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button className="btn btn-secondary" onClick={toggle}
        style={{ fontSize: 12, padding: '5px 12px' }}>
        {loading ? 'Loading…' : 'Recent ▾'}
      </button>

      {open && items.length > 0 && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200,
          background: 'var(--bg-secondary)', border: '1px solid var(--border)',
          borderRadius: 8, boxShadow: '0 6px 24px rgba(0,0,0,0.3)',
          width: 420, maxHeight: 360, overflowY: 'auto',
        }}>
          <div style={{ padding: '8px 12px 4px', fontSize: 10, color: 'var(--text-muted)',
                        fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
                        borderBottom: '1px solid var(--border)' }}>
            Recent {sourceType === 'request' ? 'Requests' : 'Batches'}
          </div>

          {items.map(item => {
            const isReq = sourceType === 'request';
            const id    = isReq ? item.request_id : String(item.batch_id);
            const date  = item.createdAt
              ? new Date(item.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
              : '—';
            const state  = isReq ? item.state       : item.status;
            const count  = isReq ? (item.expenditure_count ?? (item.expenditures || []).length)
                                 : (item.expenditure_count || '?');
            const cands  = isReq ? (item.candidate_count   ?? '?') : null;

            return (
              <div key={id} onClick={() => { onPick(id); setOpen(false); }}
                style={{ padding: '9px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>

                {/* ID — prominent */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                  <code style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)',
                                 background: 'var(--bg-secondary)', padding: '1px 6px', borderRadius: 4 }}>
                    {id}
                  </code>
                  <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10,
                    background: state === 'COMPLETED' || state === 'COMPLETE'
                      ? 'rgba(34,197,94,0.15)' : state === 'FAILED' ? 'rgba(239,68,68,0.15)'
                      : 'rgba(251,191,36,0.15)',
                    color: state === 'COMPLETED' || state === 'COMPLETE'
                      ? '#22c55e' : state === 'FAILED' ? '#ef4444' : '#fbbf24',
                  }}>
                    {state}
                  </span>
                </div>

                {/* Secondary info */}
                <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', gap: 12 }}>
                  {item.batch_name && <span>{item.batch_name}</span>}
                  <span>{count} exp{count !== 1 ? 's' : ''}</span>
                  {cands != null && <span>{cands} candidates</span>}
                  {item.matching_mode && <span>{item.matching_mode}</span>}
                  <span>{date}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Meta badge ──────────────────────────────────────────────────────────────
function Badge({ label, value }) {
  return (
    <div style={{ fontSize: 12 }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}: </span>
      <span style={{ fontWeight: 600 }}>{value ?? '—'}</span>
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
export default function DataViewer() {
  const [sourceType, setSourceType] = useState('request');
  const [inputId, setInputId]       = useState('');
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [data, setData]             = useState(null);
  const [activeTab, setActiveTab]   = useState('expenditures');

  const doLoad = async (id) => {
    const target = (id || inputId).trim();
    if (!target) return;
    setLoading(true);
    setError('');
    setData(null);
    try {
      if (sourceType === 'request') {
        const doc = await getRequestDetail(target);
        const exps  = (doc.expenditures || []).map(e => {
          const { _id, __v, ...rest } = e;
          return rest;
        });
        const cands = (doc.candidates || []).map(c => {
          const { _id, __v, ...rest } = c;
          return rest;
        });
        setData({
          meta: {
            id: doc.request_id,
            type: 'Request',
            label: doc.request_id,
            state: doc.state,
            mode: doc.matching_mode || '—',
            exp_count: exps.length,
            cand_count: cands.length > 0 ? cands.length : (doc.candidate_count || 0),
            slim: cands.length > 0,
          },
          expenditures: exps,
          candidates: cands,
        });
      } else {
        const res = await getBatchReceived(target);
        setData({
          meta: {
            id: String(res.batch_id),
            type: 'Batch',
            label: res.batch_name ? `${res.batch_id} – ${res.batch_name}` : String(res.batch_id),
            state: res.batch_status,
            mode: res.matching_mode || '—',
            exp_count: res.expenditure_count,
            cand_count: res.candidate_count,
            slim: true,
          },
          expenditures: res.expenditures || [],
          candidates: res.candidates || [],
        });
      }
      setActiveTab('expenditures');
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load');
    }
    setLoading(false);
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 40 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Data Viewer</h2>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
        Inspect the exact expenditures and award candidates received for any request or batch.
      </p>

      {/* ── Source selector ───────────────────────────────────────────────── */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>

          {/* Type toggle */}
          <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
            {['request', 'batch'].map(t => (
              <button key={t}
                onClick={() => { setSourceType(t); setData(null); setError(''); setInputId(''); }}
                style={{
                  padding: '6px 18px', fontSize: 12, border: 'none', cursor: 'pointer',
                  background: sourceType === t ? 'var(--accent)' : 'transparent',
                  color: sourceType === t ? '#fff' : 'var(--text-muted)',
                  fontWeight: sourceType === t ? 700 : 400,
                }}>
                {t === 'request' ? 'Request' : 'Batch'}
              </button>
            ))}
          </div>

          {/* ID input */}
          <input
            className="form-input"
            placeholder={sourceType === 'request' ? 'Request ID…' : 'Batch ID…'}
            value={inputId}
            onChange={e => setInputId(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && doLoad()}
            style={{ fontSize: 13, padding: '6px 10px', width: 280, fontFamily: 'monospace' }}
          />

          <button className="btn btn-primary" style={{ fontSize: 12, padding: '6px 18px' }}
            onClick={() => doLoad()} disabled={loading || !inputId.trim()}>
            {loading ? 'Loading…' : 'Load'}
          </button>

          <RecentPicker sourceType={sourceType} onPick={id => { setInputId(id); doLoad(id); }} />
        </div>

        {error && (
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--error-color, #f87171)' }}>
            ⚠ {error}
          </div>
        )}
      </div>

      {/* ── Loaded data ───────────────────────────────────────────────────── */}
      {data && (
        <>
          {/* Meta bar */}
          <div className="card" style={{ padding: '10px 16px', marginBottom: 14,
                                         display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
            <div>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', marginRight: 4 }}>{data.meta.type}</span>
              <code style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>{data.meta.label}</code>
            </div>
            <Badge label="Status" value={data.meta.state} />
            <Badge label="Mode"   value={data.meta.mode} />
            <Badge label="Expenditures"      value={data.meta.exp_count} />
            <Badge label="Award Candidates"  value={data.meta.cand_count} />
          </div>

          {/* Tab bar */}
          <div style={{ display: 'flex', gap: 0, marginBottom: 0, borderBottom: '1px solid var(--border)' }}>
            {[
              { key: 'expenditures', label: `Expenditures (${data.expenditures.length})` },
              { key: 'candidates',   label: `Award Candidates (${data.candidates.length})` },
            ].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: '8px 20px', fontSize: 13, border: 'none', cursor: 'pointer',
                  background: 'transparent',
                  color: activeTab === tab.key ? 'var(--text-primary)' : 'var(--text-muted)',
                  fontWeight: activeTab === tab.key ? 700 : 400,
                  borderBottom: activeTab === tab.key
                    ? '2px solid var(--accent)' : '2px solid transparent',
                  marginBottom: -1,
                }}>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="card" style={{ padding: 16, borderRadius: '0 0 8px 8px' }}>
            {data.meta.slim && activeTab === 'candidates' && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10,
                            padding: '6px 10px', background: 'var(--bg-secondary)', borderRadius: 4 }}>
                Fund authorization description and control description are not stored in the log
                (stripped to keep document size within limits). Full text is visible in match results.
              </div>
            )}
            {activeTab === 'expenditures' && (
              <DataTable items={data.expenditures} summaryGroups={EXP_SUMMARY}
                emptyMsg="No expenditures found for this request / batch." />
            )}
            {activeTab === 'candidates' && (
              <DataTable items={data.candidates} summaryGroups={AWD_SUMMARY}
                emptyMsg={data.candidates.length === 0 && data.meta.cand_count > 0
                  ? `${data.meta.cand_count} candidates were received but this is an older record processed before the log was added.`
                  : 'No award candidates found.'} />
            )}
          </div>
        </>
      )}
    </div>
  );
}
